import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Reveal, Label } from "../components/primitives";
import { Instagram, Facebook } from "lucide-react";
import { SERVICES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, value, onChange, type = "text", testid }) => (
  <div className="relative pt-6">
    <label className="label text-white/40 absolute top-0 left-0">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required
      data-testid={testid}
      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4A843] outline-none py-3 font-serif text-2xl transition-colors duration-300"
    />
  </div>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-52 pb-16 relative z-10">
        <Reveal><Label>Let's talk</Label></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-[200] leading-[0.9] mt-8">
            Let's work<br /><span className="italic text-gold">together.</span>
          </h1>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32 relative z-10">
        <div className="grid md:grid-cols-12 gap-12 lg:gap-20">
          {/* Info */}
          <div className="md:col-span-4 space-y-12">
            <Reveal>
              <p className="label text-white/40 mb-3">Email</p>
              <a href="mailto:gregory@yahtov.com" className="font-serif text-2xl md:text-3xl text-gold hover:text-[#E5BE5E] transition-colors duration-300">
                gregory@yahtov.com
              </a>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="label text-white/40 mb-3">Location</p>
              <p className="font-serif text-2xl md:text-3xl">Charlotte, North Carolina</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="label text-white/40 mb-3">Follow</p>
              <div className="flex gap-6 items-center">
                <a href="#" aria-label="Instagram" data-testid="contact-instagram" className="text-white/70 hover:text-gold transition-colors duration-300">
                  <Instagram strokeWidth={1.25} className="w-6 h-6" />
                </a>
                <a href="#" aria-label="Facebook" data-testid="contact-facebook" className="text-white/70 hover:text-gold transition-colors duration-300">
                  <Facebook strokeWidth={1.25} className="w-6 h-6" />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex items-center gap-4 pt-8 border-t hairline">
                <Star size={16} className="text-gold star-spin" />
                <p className="font-serif italic text-white/70 text-xl">Good work. God's way.</p>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="md:col-span-8">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <Field label="Your name" name="name" value={form.name} onChange={onChange} testid="contact-name" />
                <Field label="Email address" name="email" type="email" value={form.email} onChange={onChange} testid="contact-email" />
              </div>

              <div className="relative pt-6">
                <label className="label text-white/40 absolute top-0 left-0">Service of interest</label>
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  data-testid="contact-service"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4A843] outline-none py-3 font-serif text-2xl transition-colors duration-300 appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#0D1220]">Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title} className="bg-[#0D1220]">{s.title}</option>
                  ))}
                  <option value="Other" className="bg-[#0D1220]">Other</option>
                </select>
              </div>

              <div className="relative pt-6">
                <label className="label text-white/40 absolute top-0 left-0">Tell us about your project</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={4}
                  data-testid="contact-message"
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4A843] outline-none py-3 font-serif text-2xl transition-colors duration-300 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-3 border border-[#D4A843] text-[#D4A843] px-10 py-4 label hover:bg-[#D4A843] hover:text-[#0D1220] transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send message"}
                <span className="transition-transform duration-300 group-hover:translate-x-1">&#8594;</span>
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
